import { downloadFile, uid } from "@dhx/ts-common/core";
import {
	ActionDirection,
	DataEvents,
	Diagram,
	DiagramEvents,
	IBaseCoords,
	ItemConfig,
	ISwimlaneConfig,
} from "@dhx/ts-diagram";
import { Layout } from "@dhx/ts-layout";

import { Controls } from "./controls/Controls";
import { BaseDiagramEditor } from "./BaseDiagramEditor";
import { topbar } from "./helpers/topbar";
import { ShapesBar } from "./ShapesBar";
import { Sidebar } from "./Sidebar";
import { BlockSelection } from "./BlockSelection";
import { DiagramEditorEvents, IDefaultEditorConfig, ITargetOption, ICoords } from "./types";
import { IDataItem } from "@dhx/ts-data";
import { Id } from "@dhx/ts-common/types";
import {
	getDistanceEblowLine,
	getDistancePercentage,
	getLinePercentage,
	getNearPointStraightLine,
} from "@dhx/ts-diagram/sources/helpers/lineHelpers";
import { LineText } from "@dhx/ts-diagram/sources/components/Lines/LineText";

export class FreeEditor extends BaseDiagramEditor {
	public config: IDefaultEditorConfig;
	private _changeMode: boolean;
	private _shapesBar: ShapesBar;
	private _copiedItems: ItemConfig[] = [];

	constructor(container: string | HTMLElement, config: IDefaultEditorConfig) {
		super(container, config);
	}

	protected _initDiagram() {
		this.diagram = new Diagram(null, {
			type: "default",
			select: true,
			toolbar: this._getToolbarConfig(this.config),
			gridStep: this.config.gridStep,
			margin: {
				y: 70,
				x: this.config.reservedWidth,
			},
			lineGap: this.config.lineGap,
			defaults: this._getDefaults(),
			scale: this.config.scale,
			autoplacement: this.config.autoplacement,
			defaultShapeType: this.config.shapeType,
		});
	}
	protected _initUI(container) {
		this._layout = new Layout(container, {
			height: "100%",
			css: "dhx_diagram_editor_default",
			rows: [
				{
					id: "top",
					css: "dhx_topbar",
				},
				{
					css: "dhx_diagram_editor",
					cols: [
						{
							id: "left",
							width: this.config.shapeBarWidth,
							css: "dhx_diagram_editor_shapebar__container dhx_shapesbar--with-width",
						},
						{
							id: "center",
							css: "dhx_diagram_editor__main",
						},
						{
							id: "right",
							css: "dhx_diagram_editor__sidebar dhx_diagram_editor__sidebar_default",
						},
					],
				},
			],
		});

		this.diagram.config.$svg = size => {
			const id = this.diagram.selection.getId();
			const item = this.diagram.data.getItem(id);
			if (this._selectionBox && this._selectionBox.isValid()) {
				return this._selectionBox.render();
			}
			return this._blockSelection.getSelected().length ? null : this._resizer.render(item, size);
		};
		this.diagram.data.parse([
			{
				id: uid(),
				type: "rectangle",
				text: "",
				x: 0,
				y: 0,
			},
		]);
		this.diagram.events.on(DataEvents.load, () => {
			this.history.reset();
			this._checkEditMode();
		});
		this._shapesBar = new ShapesBar(null, {
			events: this.events,
			diagram: this.diagram,
			shapeSections: this.config.shapeSections,
			defaults: this.diagram.config.defaults,
			margin: this.config.gapPreview,
			scale: this.config.scalePreview,
		});

		this._sidebar = new Sidebar(
			this.diagram,
			{
				showGridStep: this.config.controls.gridStep,
			},
			this.events
		);

		this._blockSelection = new BlockSelection(this.diagram);
		this._resizer = new Controls(this.events, this.diagram, this._blockSelection);

		this._layout.getCell("top").attach(topbar, this);
		this._layout.getCell("right").attach(this._sidebar.getUI());
		this._layout.getCell("left").attach(this._shapesBar);
		this._layout.getCell("center").attach(this.diagram);

		this._checkEditMode();
	}
	protected _showConnectPoints(id: Id, toggle?: boolean) {
		const item = this.diagram.data.getItem(id);
		if (item.$item.getBaseType() === "shape") {
			if (toggle) {
				item.$connectMode = !item.$connectMode;
				this._resizer.toggleNearShape(item);
			} else {
				this._resizer.setNearShape(item);
			}
		}
	}
	protected _setHandlers() {
		super._setHandlers();
		this.diagram.events.on(DiagramEvents.shapeIconClick, action => {
			const item = this.diagram.selection.getItem();
			const id = item.id;

			if (!this.events.fire(DiagramEditorEvents.beforeShapeIconClick, [action, item])) {
				return;
			}

			switch (action) {
				case "copy":
					this._copyItems();
					this._pasteItems();
					break;
				case "remove":
					if (item.$item.getBaseType() === "line" && item.$subSelected) {
						this._removeLineTitle(item);
						return;
					}
					this._removeItem(id);
					break;
				case "connect":
					this._showConnectPoints(this.diagram.selection.getId(), true);
					break;
				case "removePoint":
					item.points.splice(item.$selectedPoint, 1);
					item.$selectedPoint = null;
					this.diagram.paint();
					break;
				case "addColumnFirst":
					this._addSwimlaneCells(item, "left");
					break;
				case "addColumnLast":
					this._addSwimlaneCells(item, "right");
					break;
				case "addRowFirst":
					this._addSwimlaneCells(item, "up");
					break;
				case "addRowLast":
					this._addSwimlaneCells(item, "down");
					break;
				case "lineTextAutoPositionEnable":
					this._changeLineTextAutoPosition(item, false);
					break;
				case "lineTextAutoPositionDisable":
					this._changeLineTextAutoPosition(item, true);
					break;
				case "addLineText":
					this._addLineText(item);
					break;
			}
			this.events.fire(DiagramEditorEvents.afterShapeIconClick, [
				action,
				this.diagram.data.getItem(id),
			]);
		});
		this.diagram.events.on(DiagramEvents.itemMouseOver, id => {
			const selected = this.diagram.selection.getId();
			const item = this.diagram.data.getItem(id);
			if (id != selected && item.$item.getBaseType() === "shape") {
				this._showConnectPoints(id);
			}
		});
		this.events.on(DiagramEditorEvents.exportData, () => {
			const data = JSON.stringify(this.serialize());
			downloadFile(data, "data.json", "text/json");
		});
		this.events.on(DiagramEditorEvents.autoLayout, () => {
			this.diagram.autoPlace({
				root: this.diagram.selection.getId(),
				mode: this.config.autoplacement?.mode,
				graphPadding: this.config.autoplacement?.graphPadding,
			});
			this.paint();
		});
		this.events.on(DiagramEditorEvents.shapesUp, item => {
			if (this._changeMode) {
				const selected = this.diagram.selection.getId();
				this.diagram.data.update(selected, { type: item.type });
				this.diagram.paint();
				this._changeMode = false;
				this._layout.getCell("center").attach(this.diagram);
				return;
			}
			const diagramRect = this.diagram.getRootView().node.el.getBoundingClientRect();
			if (item.$x < diagramRect.left || item.$x > diagramRect.right) {
				this._addItem({ ...item, x: 0, y: 0 });
				return;
			}
			const coords = this._resizer.getPoint(item.$x, item.$y);
			coords.x = Math.round(coords.x / this.config.gridStep) * this.config.gridStep;
			coords.y = Math.round(coords.y / this.config.gridStep) * this.config.gridStep;

			const newItem = this.diagram.data.getItem(this._addItem({ ...item, x: coords.x, y: coords.y }));
			this._addItemToGroup(newItem);
		});
		this.events.on(DiagramEditorEvents.changeGridStep, step => {
			this.diagram.config.gridStep = step;
			this.config.gridStep = step;
			this.paint();
		});
		this.events.on(
			DiagramEditorEvents.beforeItemMove,
			(_event: MouseEvent, id: Id, _coords: ICoords, $mov: ICoords) => {
				return this._moveChilds(id, $mov);
			}
		);
		this.events.on(DiagramEditorEvents.afterShapeMove, () => {
			const selectedShape = this.diagram.selection.getItem();
			if (!selectedShape) {
				return;
			}
			const parent = this.diagram.data.getItem(selectedShape?.$group);

			this.diagram.data.mapVisible(item => {
				const { id, $item, groupChildren, exitArea } = item;
				const enableGroup = $item.getBaseType() === "group" && open;

				if (enableGroup) {
					if (exitArea.groupBehavior === "unbound" || parent?.id != id) {
						const isGroupChild = groupChildren.find(shape => shape == selectedShape.id);

						if (isGroupChild) {
							const index = groupChildren.findIndex(id => id == selectedShape.id);
							groupChildren.splice(index, 1);

							this.diagram.data.update(id, {
								groupChildren,
							});
							this.diagram.data.update(selectedShape.id, { $group: null }, true);
						}
					}
					if (
						parent?.id != id &&
						(!parent ||
							parent?.exitArea.groupBehavior === "unbound" ||
							(parent?.exitArea.groupBehavior !== "unbound" &&
								parent?.groupChildren.findIndex(i => i == id) !== -1))
					) {
						const percent = $item.getLocatePercent(selectedShape.$item.getBox());
						this._setItemIndicator(item, percent);
					}
				}
			});
		});
		this.events.on(DiagramEditorEvents.afterGroupMove, () => {
			const selectedGroup = this.diagram.selection.getItem();
			if (!selectedGroup || selectedGroup.$item.getBaseType() === "swimlane") {
				return;
			}
			const parent = this.diagram.data.getItem(selectedGroup?.$group);

			const excludeItems = {};
			excludeItems[selectedGroup.id] = selectedGroup;
			this.diagram.data.eachChild(selectedGroup.id, item => (excludeItems[item.id] = item));

			this.diagram.data.mapVisible(item => {
				const { id, $item, groupChildren, exitArea } = item;
				const enableGroup = $item.getBaseType() === "group" && open;

				if (!excludeItems[id] && enableGroup) {
					if (exitArea.groupBehavior === "unbound" || parent?.id != id) {
						const isGroupChild = groupChildren.find(shape => shape == selectedGroup.id);

						if (isGroupChild) {
							const index = groupChildren.findIndex(id => id == selectedGroup.id);
							groupChildren.splice(index, 1);

							this.diagram.data.update(id, {
								groupChildren,
							});
							this.diagram.data.update(selectedGroup.id, { $group: null }, true);
						}
					}
					if (
						parent?.id != id &&
						(!parent ||
							parent?.exitArea.groupBehavior === "unbound" ||
							(parent?.exitArea.groupBehavior !== "unbound" &&
								parent?.groupChildren.findIndex(i => i == id) !== -1))
					) {
						const percent = $item.getLocatePercent(selectedGroup.$item.getBox());
						this._setItemIndicator(item, percent);
					}
				}
			});
		});
		this.events.on(DiagramEditorEvents.itemMoveEnd, () => {
			const selectedItem = this.diagram.selection.getItem();
			if (selectedItem) {
				this._addItemToGroup(selectedItem);
			}
		});
	}
	protected _getGroupTarget(item: IDataItem, option: ITargetOption, target = null): IDataItem {
		if (item.id == option.selected.id || item.id == option.exclude) {
			return target;
		}
		const { id, open, groupChildren, $item, entryArea } = item;
		const locatePercent = $item.getLocatePercent(option.selected.$item.getBox());

		if (open && locatePercent >= entryArea.catchArea) {
			target = item;
			if (groupChildren.length) {
				this.diagram.data.eachChild(
					id,
					child => {
						if (child.$item.getBaseType() === "group") {
							target = this._getGroupTarget(child, option, target);
						}
					},
					false
				);
			}
		}
		return target;
	}
	protected _copyItems(): void {
		this._copiedItems = [];
		const selectionItem = this.diagram.selection.getItem();

		if (selectionItem && !selectionItem.$group) {
			this._copiedItems.push(selectionItem);

			const type = selectionItem.$item.getBaseType();

			if (type === "group" || type === "swimlane") {
				const copiedId = {};

				this.diagram.data.eachChild(selectionItem.id, ({ id }) => {
					copiedId[id] = true;
				});

				this.diagram.data.forEach(item => {
					if (item?.$item?.getBaseType() === "line" && copiedId[item?.from] && copiedId[item?.to]) {
						this._copiedItems.push(item);
					}
				});
			}
		} else {
			this._blockSelection.getSelected().forEach(id => {
				const blockSelectionItem = this.diagram.data.getItem(id);
				if (!blockSelectionItem.$group) {
					this._copiedItems.push(blockSelectionItem);
				}
			});
		}
	}
	protected _pasteItems(): void {
		if (!this._copiedItems.length) {
			return;
		}
		this._clearSelection();

		const linkParents = {};
		const swimlanePullId = {};

		const addGroup = (item: IDataItem, offset = 0): Id => {
			if (item.$group) {
				offset = offset + item.$gx;
			} else {
				offset = item.x + item.width + 40 + offset;
			}

			const groupChildren = [];

			let id;
			if (item.$item.getBaseType() === "swimlane") {
				id = this.diagram.data.add({
					...item,
					groupChildren,
					id: uid(),
					x: offset,
					layout: [],
				});
			} else {
				id = this.diagram.data.add({
					...item,
					groupChildren,
					id: uid(),
					x: offset,
				});
			}

			this.diagram.data.eachChild(
				item.id,
				child => {
					let childId;
					if (child.$item.getBaseType() === "group") {
						childId = addGroup(child, offset);
						swimlanePullId[child.id] = childId;
					} else {
						childId = this.diagram.data.add({
							...child,
							$group: null,
							id: uid(),
							x: offset + child.$gx,
						});
					}
					groupChildren.push(childId);
					linkParents[child.id] = childId;
				},
				false
			);
			if (item.$item.getBaseType() === "swimlane") {
				const layout = JSON.parse(JSON.stringify([...item.layout]));

				for (let i = 0; i < layout.length; i++) {
					const row = layout[i];
					for (let k = 0; k < row.length; k++) {
						const cellId = row[k];
						layout[i][k] = swimlanePullId[cellId];
					}
				}
				this.diagram.data.update(id, {
					groupChildren,
					layout,
				});
			} else {
				this.diagram.data.update(id, {
					groupChildren,
				});
			}

			linkParents[item.id] = id;

			return id;
		};

		this._copiedItems.forEach(item => {
			let id;
			switch (item.$item.getBaseType()) {
				case "shape":
					id = this.diagram.data.add({
						...item,
						id: uid(),
						x: item.x + item.width + 40,
					});
					linkParents[item.id] = id;
					break;
				case "group":
				case "swimlane":
					id = addGroup(item);
					break;
				case "line":
					if (linkParents.hasOwnProperty(item.from) && linkParents.hasOwnProperty(item.to)) {
						this.diagram.data.add({
							...item,
							id: uid(),
							from: linkParents[item.from],
							to: linkParents[item.to],
						});
					}
					break;
			}

			this._addSelection(id);
		});
	}
	protected _addSelection(id: Id): void {
		if (this._copiedItems.length === 1) {
			this.diagram.selection.add(id);
		} else {
			this._blockSelection.add(id);
		}
	}
	protected _clearSelection(): void {
		this._blockSelection.clearSelection();
		this.diagram.selection.remove();
	}
	protected _initHotkeys() {
		document.addEventListener("keydown", event => {
			const ctrlKey = event.ctrlKey;
			const cKey = event.key === "c";

			if (ctrlKey && cKey && this._sidebar.isItemsSelected()) this._copiedItems = [];
		});
		super._initHotkeys();
		this._keyManager.addHotKey("ctrl+a", (e: KeyboardEvent) => {
			if (!this._sidebar.isItemsSelected()) {
				e.preventDefault();
				this.diagram.data.forEach(item => {
					if (!item.$group) {
						this.diagram.data.update(item.id, { $blockSelected: "add" || "create" }, true);
					}
				});
				this.paint();
			}
		});
		this._keyManager.addHotKey("ctrl+c", () => {
			if (!this._sidebar.isItemsSelected()) {
				this._copyItems();
			}
		});
		this._keyManager.addHotKey("ctrl+v", () => {
			if (!this._sidebar.isItemsSelected()) {
				this._pasteItems();
			}
		});
		this._keyManager.addHotKey("ctrl+d", (e: KeyboardEvent) => {
			e.preventDefault();
			this._copyItems();
			this._pasteItems();
		});
	}
	protected _addItemToGroup(selectedItem: IDataItem): void {
		let targetGroup = null;

		this.diagram.data.mapVisible(item => {
			const { id, groupChildren, $item } = item;
			const type = $item.getBaseType();
			if (type === "group" && type !== "swimlane" && selectedItem.$item.getBaseType() !== "swimlane") {
				if (!targetGroup) {
					targetGroup = this._getGroupTarget(item, {
						selected: selectedItem,
					});
				}
				this.diagram.data.update(
					id,
					{
						$captureArea: "out",
					},
					true
				);
				const isGroupChild = groupChildren.find(item => item == selectedItem.id);

				if (isGroupChild) {
					const index = groupChildren.findIndex(id => id == selectedItem.id);
					groupChildren.splice(index, 1);

					this.diagram.data.update(item.id, {
						groupChildren,
					});
					this.diagram.data.update(selectedItem.id, { $group: null }, true);
				}
			}
		});

		if (targetGroup) {
			const { id, groupChildren } = targetGroup;
			const isGroupChild = id == selectedItem.$group;

			if (!isGroupChild) {
				this.diagram.data.update(id, {
					groupChildren: [...groupChildren, selectedItem.id],
				});
			}
		}
		this._setParentBox(selectedItem.id);
	}
	protected _addItem(item: ItemConfig): Id {
		let id;

		delete item.$key;
		delete item.$x;
		delete item.$y;

		switch (item.type) {
			case "$swimlane":
				id = this._addSwimlaneItem(item as ISwimlaneConfig);
				break;
			default:
				id = this.diagram.data.add({
					...item,
					id: uid(),
				});
				break;
		}

		return id;
	}
	protected _addSwimlaneItem(item: any): Id {
		const newItem = JSON.parse(JSON.stringify(item));
		const layout = newItem.layout;
		const pull = {};

		for (let i = 0; i < layout.length; i++) {
			const row = layout[i];
			for (let k = 0; k < row.length; k++) {
				const cell = row[k];
				if (!pull[cell]) {
					const id = uid();
					newItem.layout[i][k] = pull[cell] = id;
				} else {
					newItem.layout[i][k] = pull[cell];
				}
			}
		}

		return this.diagram.data.add({
			...newItem,
			layout,
			width: newItem.width * 2,
			height: newItem.height * 2,
			id: uid(),
		});
	}
	protected _moveChilds(id: Id, mov: IBaseCoords): boolean {
		const item = this.diagram.data.getItem(id);
		const selectItem = this.diagram.selection.getItem();

		if (item.$item.isFixed()) {
			return false;
		}
		if (item.$group) {
			const group = this.diagram.data.getItem(item.$group);
			if (group.$group) {
				this._moveChilds(group.id, mov);
			}
			return group.$item.trackChildMove(selectItem.$item.getBox(), {
				x: mov.x,
				y: mov.y,
			});
		}
	}
	protected _setParentBox(id: Id): void {
		if (id) {
			const item = this.diagram.data.getItem(id);
			if (item.$item.getBaseType() === "group") {
				item.$item.setMinBox();
			}
			if (item.$group) {
				this._setParentBox(item.$group);
			}
		}
	}
	protected _setItemIndicator({ entryArea, $captureArea, id }: IDataItem, percent: number): void {
		if (percent >= entryArea.catchArea) {
			if ($captureArea !== "over") {
				this.diagram.data.update(
					id,
					{
						$captureArea: "over",
					},
					true
				);
			}
		} else if (percent !== 0) {
			if ($captureArea !== "partially") {
				this.diagram.data.update(
					id,
					{
						$captureArea: "partially",
					},
					true
				);
			}
		} else {
			if ($captureArea !== "out") {
				this.diagram.data.update(
					id,
					{
						$captureArea: "out",
					},
					true
				);
			}
		}
	}
	protected _addSwimlaneCells(item, dir: ActionDirection): void {
		const cellId =
			item.layout[dir === "down" ? item.layout.length - 1 : 0][
				dir === "right" ? item.layout[0].length - 1 : 0
			];
		const type = dir === "left" || dir === "right" ? "col" : "row";

		this.diagram.cellManager.setSwimlane(item.id);
		this.diagram.cellManager.add(this.diagram.cellManager.getCellIndex(cellId, type), dir, true);
	}
	private _changeLineTextAutoPosition(item: IDataItem, action: boolean): void {
		item.title.text.find(i => i.id === item.$subSelected).autoPosition = action;
		this.diagram.data.update(item.id, item);
	}
	private _addLineText(line: IDataItem): void {
		const points = line.points;
		const cp = this.diagram._lastItemClickCoords;

		let distance = 50;

		if (line.connectType === "straight" || points.length === 2) {
			const sp = points[0];
			const ep = points[points.length - 1];
			const lp = getNearPointStraightLine(sp, ep, cp);
			const factor = getLinePercentage(sp, ep, lp);

			distance = getDistancePercentage(factor);
		} else {
			distance = getDistanceEblowLine(line, cp);
		}

		if (!line.title) {
			line.title = {};
			line.$item.setDefaults(line);
		}

		const titleId = uid();
		const lineTextConfig: any = {
			type: "$linetext",
			id: titleId,
			text: "Text",
			distance,
		};

		line.title.text.push(lineTextConfig);

		lineTextConfig.$item = new LineText(lineTextConfig, {
			data: this.diagram.data,
			parent: line,
		});

		line.title.text = line.title.text.map(text => {
			if (text.id === titleId) {
				return {
					...text,
					...lineTextConfig.$item.config,
				};
			}
			return text;
		});

		this.diagram.data.update(line.id, line);
		this.diagram.selection.add(line.id, titleId);
	}
}
